import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { AngularFirestore, AngularFirestoreCollection } from "@angular/fire/compat/firestore";
import { Router } from "@angular/router";
import { buildCommunityPostPath, CommunitiesService, EventBusService, HelperService, UserService, WindowService, environment, featuredRailApplies, isFeaturedNow } from "@mypxplat/xplat/core";

import { ChannelBaseComponent } from "@mypxplat/xplat/features";
import { AppService, BetaService, FirebaseService, WebCommunityService } from "@mypxplat/xplat/web/core";
import { CreatePostInputComponent } from "../create-post-input/create-post-input.component";
import { jiraBrowseUrlFromTicketKey } from "../../../../utils/beta-jira-browse-url";
import { ClipboardService } from "ngx-clipboard";
import { filter, map, take, takeUntil } from "rxjs/operators";
import { BetaCommunityService } from "@mypxplat/xplat/core";
import { SearchClient, searchClient } from "@algolia/client-search";

/**
 * "Now" for Algolia filter strings, to the minute. A raw `Date.now()` made every request
 * unique, so neither Algolia's query cache nor the client's could ever answer one; the minute
 * of slack only affects a post whose scheduled time passed within it.
 */
const algoliaNow = () => Math.floor(Date.now() / 60_000) * 60_000;

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-channel",
  templateUrl: "channel.component.html",
  standalone: false,
})
export class ChannelComponent extends ChannelBaseComponent {
  private _selectedChannel: any;
  @Input() searchQuery: string;
  @Input() set selectedChannel(channel) {
    const changed = channel?.string_id !== this._selectedChannel?.string_id;
    this._selectedChannel = channel;
    if (channel && changed) {
      if (this.communityService.restoringFromPostDetail) {
        // Returning from post detail — data is already restored on the service.
        // Just restore Algolia pagination state and signal completion.
        this.communityService.restoringFromPostDetail = false;
        const cache = this.communityService.communityListBackCache;
        if (cache?.algoliaState) {
          this.usingAlgolia = true;
          this.algoliaPage = cache.algoliaState.algoliaPage;
          this.algoliaTotalPages = cache.algoliaState.algoliaTotalPages;
          this.currentAlgoliaFilters = cache.algoliaState.currentAlgoliaFilters;
          this.currentAlgoliaQuery = cache.algoliaState.currentAlgoliaQuery;
          this.usingActivityIndex = !!cache.algoliaState.usingActivityIndex;
          this.communityService.activePostsLimitReached = this.algoliaPage >= this.algoliaTotalPages - 1;
        }
        this.communityService.sortActiveParentPosts();
        if (cache?.viewedPostId) {
          this.highlightPostId = cache.viewedPostId;
          this._win.setTimeout(() => {
            this.highlightPostId = null;
          }, 2000);
        }
        // Read before `finishRestore` nulls the cache — the parent needs it to put the
        // member back on the post they opened rather than on a stale pixel offset.
        const scrollToPostId = cache?.viewedPostId;
        const finishRestore = () => {
          this.communityService.communityListBackCache = null;
          this.loading = false;
          this._win.setTimeout(() => {
            this.recomputeUnreadPosts();
            this.postsLoaded.next({ scrollToPostId });
          });
        };
        if (!cache?.algoliaState && this.betaStringId) {
          this.refreshBetaFacets("").then(finishRestore);
        } else {
          finishRestore();
        }
      } else if (this.tryRestoreFromBackCache(channel)) {
        // Came back from a post detail by some route other than the in-app back button —
        // browser back, a swipe, an external link. `goBack()` is what sets
        // `restoringFromPostDetail`, so those paths land here instead and used to refetch
        // the whole feed. The snapshot is still on the service, so use it.
      } else {
        this._win.setTimeout(() => {
          this.initPosts();
        }, 10);
      }
    }
  }

  /**
   * Repopulate the feed from `communityListBackCache` instead of hitting the network.
   *
   * Only fires when the snapshot carries a `viewedPostId` — that field is set exclusively by
   * `enrichCommunityListBackCacheForPostNavigation`, so its presence is what distinguishes
   * "returning from a post detail" from any other arrival at this channel. Without that
   * check a stale snapshot could resurface long after it stopped matching what's live.
   *
   * The cache is one-shot: `restoreFromCommunityListBackCache` nulls it on the way out, so a
   * second visit fetches fresh. Returns whether it restored.
   */
  private tryRestoreFromBackCache(channel: any): boolean {
    const cache = this.communityService.communityListBackCache;
    if (!cache?.viewedPostId) return false;

    const scrollToPostId = cache.viewedPostId;
    const restored = this.communityService.restoreFromCommunityListBackCache(this.betaStringId, channel);
    if (!restored) return false;

    if (restored.algoliaState) {
      this.usingAlgolia = true;
      this.algoliaPage = restored.algoliaState.algoliaPage;
      this.algoliaTotalPages = restored.algoliaState.algoliaTotalPages;
      this.currentAlgoliaFilters = restored.algoliaState.currentAlgoliaFilters;
      this.currentAlgoliaQuery = restored.algoliaState.currentAlgoliaQuery;
      this.usingActivityIndex = !!restored.algoliaState.usingActivityIndex;
      this.communityService.activePostsLimitReached = this.algoliaPage >= this.algoliaTotalPages - 1;
    }
    this.communityService.sortActiveParentPosts();

    this.highlightPostId = scrollToPostId;
    this._win.setTimeout(() => {
      this.highlightPostId = null;
    }, 2000);

    this.loading = false;
    this._win.setTimeout(() => {
      this.recomputeUnreadPosts();
      this.postsLoaded.next({ scrollToPostId });
    });
    return true;
  }

  /**
   * Feed layout: "detailed" (full posts) or "list" (compact mobile-style rows).
   *
   * TWO owners, deliberately kept apart. Beta drives it from the filter sidebar via
   * `selectedBetaView`; regular communities drive it from the toggle beside the channel
   * title, persisted per community in localStorage. The seed here is the beta value
   * because only beta had the setting before this; `ngOnInit` overrides it for non-beta.
   */
  public displayType: any = this.betaCommunityService.getViewMode("detailed");
  @Input() set selectedBetaView(view) {
    this.displayType = view;
    this.reloadIfRepliesDeferred();
  }

  /**
   * Which posts the member has not been shown, by id — the feed marks each one rather than
   * drawing a boundary between them.
   *
   * Per post rather than a single "New posts" line because a line only means anything while
   * the feed is in newest-first `created` order. Marking the posts themselves survives any
   * ordering, including sorting by newest reply, where seen and unseen interleave.
   *
   * A map rather than a predicate so the template binds a property lookup; recomputed when
   * the feed loads rather than per change-detection pass.
   */
  public unreadPostIds: Record<string, boolean> = {};

  /**
   * Posts opened during this visit. Reading one clears its mark in the feed immediately,
   * and it has to STAY clear — the feed recomputes on every reload (coming back from the
   * detail is one), and without this the post the member just read would light up again.
   *
   * Session-scoped on purpose: it is a view concern. The durable record is the post's view
   * record, which the detail writes on the same click (`markPostViewed`) — this just saves
   * the round of change detection before that lands.
   */
  private _openedPostIds = new Set<string>();

  /**
   * A row was opened. Drop its highlight before handing the open up to the shell, so the
   * feedback lands on the click rather than a render later.
   */
  onPostOpened(postId: string) {
    const id = postId ? String(postId) : "";
    if (id) {
      this._openedPostIds.add(id);
      if (this.unreadPostIds[id]) {
        const next = { ...this.unreadPostIds };
        delete next[id];
        this.unreadPostIds = next;
      }
    }
    this.openPost.next(postId);
  }

  /**
   * The detailed feed shows every post in full, so there is nothing to open — a post that sat
   * on screen long enough counts as read (`mypSeenInView`: half of it visible, or half the
   * viewport, for a second). Recorded exactly as opening it would be, so it stays read across
   * sessions and devices, and `postViews$` clears its row.
   */
  onPostSeenInFeed(postId: string) {
    const id = postId ? String(postId) : "";
    if (!id || this.betaStringId) return;
    // Not added to `_openedPostIds`: that would keep it read for the rest of the visit, while
    // the view record alone lets a reply arriving later light it up again.
    this.communityService.markPostViewed(id);
  }

  /** See `WebCommunityService.postActivityMillis` — shared with the Communities hub's feed. */
  private postActivityMillis(post: any): number {
    return this.communityService.postActivityMillis(post);
  }

  /**
   * Point the service's reply loading at this layout before a load: the list rows need no
   * replies, the detailed layout renders them inline.
   */
  private syncReplyDeferral() {
    this.communityService.deferRepliesInFeed = this.displayType === "list";
  }

  /**
   * After a switch to the detailed layout, reload if the feed was loaded without its replies —
   * otherwise every thread would render with none.
   */
  private reloadIfRepliesDeferred() {
    if (this.displayType !== "detailed" || this.loading) return;
    if ((this.communityService.activeParentPosts || []).some((post: any) => post._repliesDeferred)) this.initPosts();
  }

  /**
   * The Algolia index for this feed. The recent-activity ordering has its own replica, when
   * one is configured (see `communityAlgoliaActivityIndex`) and it hasn't failed this session.
   */
  private algoliaIndexFor(activity: boolean): string {
    if (activity) {
      const activityIndex = this.betaStringId ? this.env.betaCommunityAlgoliaActivityIndex : this.env.communityAlgoliaActivityIndex;
      if (activityIndex && !ChannelComponent.activityIndexFailed) return activityIndex;
    }
    return this.betaStringId ? "prs_beta_community_posts_sorted_created_desc" : this.env.communityAlgoliaIndex;
  }

  /** Set when the activity replica errors (not created yet, say), so the feed stops asking it. */
  private static activityIndexFailed = false;

  /** Whether the current Algolia feed is the recent-activity replica, for "Show more". */
  private usingActivityIndex = false;

  /**
   * Per post, not per channel: a row is unread when its thread has activity from someone else
   * newer than BOTH your last visit to that post and the community's read floor (see
   * `WebCommunityService.postUnreadFloor`). Only opening the post, or "Mark all read", clears
   * it — visiting a channel no longer does. The rail's counts are separate and still per
   * channel.
   *
   * A post whose view record hasn't been prefetched yet is left alone rather than guessed at;
   * `postViews$` re-runs this when the records land, so rows light up a moment after a page
   * does instead of flashing on and off.
   */
  recomputeUnreadPosts() {
    const floor = this.betaStringId ? 0 : this.communityService.postUnreadFloor(this.communityStringId);
    if (!floor) {
      this.unreadPostIds = {};
      return;
    }
    const unread: Record<string, boolean> = {};
    (this.communityService.activeParentPosts || []).forEach((post: any) => {
      const id = post?.id ? String(post.id) : "";
      if (!id || this._openedPostIds.has(id)) return;
      const activity = this.postActivityMillis(post);
      if (!activity || activity <= floor) return;
      const lastViewed = this.communityService.peekPostLastViewed(id);
      if (lastViewed === undefined || activity <= lastViewed) return;
      unread[id] = true;
    });
    this.unreadPostIds = unread;
  }

  /** Toggle handler for the non-beta feed. Persisted per community, not per session. */
  selectViewMode(mode: "list" | "detailed") {
    if (this.displayType === mode) return;
    this.displayType = mode;
    this.communityService.setCommunityViewMode(this.communityStringId, mode);
    this.viewModeChanged.emit(mode);
    this.reloadIfRepliesDeferred();
  }

  get selectedChannel() {
    return this._selectedChannel;
  }

  private _selectedTopic: any;
  @Input() set selectedTopic(topic) {
    this._selectedTopic = topic;
    // Resolved here rather than in the template. These are Firestore Timestamps, so the
    // header needs `.toDate()` before the date pipe can take them — and a method call in a
    // binding re-runs on every change-detection pass (the repo's pre-commit rule).
    this.topicStartDate = topic?.start_date?.toDate ? topic.start_date.toDate() : topic?.start_date || null;
    this.topicArchivedDate = topic?.date_archived?.toDate ? topic.date_archived.toDate() : topic?.date_archived || null;
    if (topic) {
      this._win.setTimeout(() => {
        this.initPosts();
      }, 10);
    }
  }

  get selectedTopic() {
    return this._selectedTopic;
  }

  /** Pre-resolved `selectedTopic` dates — see the setter above. */
  public topicStartDate: Date | null = null;
  public topicArchivedDate: Date | null = null;
  @Input() betaStringId: string;
  /**
   * Community this channel feed belongs to. Defaults to Fender Studio Pro. Task #33
   * plumbs this into Algolia + Firestore filters — right now it's just accepted so
   * `<myp-channel [communityStringId]>` in the parent template doesn't error.
   */
  @Input() communityStringId: string = "fender-studio-pro";
  public betaCollection: string;
  @Input() postType = "channels";
  public queuedPosts: Array<any>;
  @ViewChild("pagingContainer", { static: false }) pagingContainer: ElementRef;
  @ViewChild("createPostInput") createPostInput: CreatePostInputComponent;
  @Output() channelSelected: EventEmitter<any> = new EventEmitter();
  @Output() topicSelected: EventEmitter<any> = new EventEmitter();
  @Output() postsLoaded: EventEmitter<any> = new EventEmitter();
  /** Bubbles a list row's open request up to `<myp-community>`, which owns the pane. */
  @Output() openPost: EventEmitter<string> = new EventEmitter();
  /** Whether the shell can host a split pane right now. Drives row click behaviour. */
  @Input() splitPaneActive = false;
  /** Post currently in the pane, so its row can be highlighted. */
  @Input() selectedPostId: string | null = null;
  /** Feed column is too narrow for reaction pills on a row; the detail still shows them. */
  @Input() compactList = false;
  /** Fires when the member flips list/detailed, so the shell can re-evaluate the pane. */
  @Output() viewModeChanged: EventEmitter<string> = new EventEmitter();
  @Output() resetFilters: EventEmitter<any> = new EventEmitter();
  public loading: boolean = true;
  public shouldShowPagination: boolean;
  public isBetaManager: boolean;
  protected currentTime: number = new Date().getTime();
  private algoliaClient: SearchClient;
  public algoliaPage: number = 0;
  public algoliaTotalPages: number = 0;
  public algoliaResultCount: number = 0;
  public usingAlgolia: boolean = false;
  private currentAlgoliaFilters: string;
  public currentAlgoliaQuery: string;
  public highlightPostId: string;
  /**
   * Whether paging past the first page is behind the FSP subscription.
   *
   * It always was, for every community — which is Fender Studio Pro's paywall leaking into
   * communities that don't have one. In Tone Master Pro (`participation_condition: "none"`)
   * a signed-in non-subscriber got "Upgrade to see more posts" while a SIGNED-OUT visitor got
   * the normal "Show more", which is backwards. Reading is open in every community; only
   * FSP-gated ones keep the paywall.
   */
  public pagingNeedsSubscription = false;

  constructor(
    userService: UserService,
    public communityService: WebCommunityService,
    public db: AngularFirestore,
    public appService: AppService,
    private _win: WindowService,
    public eventBusService: EventBusService,
    public router: Router,
    public betaService: BetaService,
    public helperService: HelperService,
    private _clipboardService: ClipboardService,
    public betaCommunityService: BetaCommunityService,
    public fbService: FirebaseService,
    public communitiesService: CommunitiesService
  ) {
    super(userService);
  }

  ngOnInit() {
    // Recomputed on every emission so a community loaded after mount (or an in-session
    // upgrade) settles the paging prompt without a reload.
    this.communitiesService.communities$.pipe(takeUntil(this.destroy$)).subscribe(() => this.updatePagingGate());
    this.userService.user$.pipe(takeUntil(this.destroy$)).subscribe(() => this.updatePagingGate());
    // Unread rows depend on view records (prefetched as pages load) and the read floor (on the
    // profile) — recompute when either arrives or changes. The profile only matters the first
    // time it loads; after that the floor's own changes come through `postViews$`.
    this.communityService.postViews$.pipe(takeUntil(this.destroy$)).subscribe(() => this.recomputeUnreadPosts());
    this.communityService.firebaseProfile$
      .pipe(
        filter((profile) => !!profile),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.recomputeUnreadPosts());
    this.updatePagingGate();
    this.isBetaManager = this.user?.groups?.length ? this.user.groups.some((group: any) => group.name === "beta_manager") : false;
    if (this.betaStringId) this.betaCollection = "beta_community_posts";
    // Non-beta feeds read their own stored layout. Beta keeps whatever `selectedBetaView`
    // pushes down, so don't clobber it here.
    if (!this.betaStringId) this.displayType = this.communityService.getCommunityViewMode(this.communityStringId);
    this.communityService.getAvailableSkills();
    this.algoliaClient = searchClient(this.env.algolia.app_id, this.env.algolia.apiKey);
    // A post featured or un-featured from the rail, the composer or a post's menu moves out of
    // or back into this feed without a reload. Un-featuring reloads, so the post lands in its
    // place in the order rather than at the top.
    this.communityService.featuredChanged$.pipe(takeUntil(this.destroy$)).subscribe(({ communityStringId, post, featured }) => {
      if (communityStringId !== this.communityStringId || !this.featuredRailActive()) return;
      if (featured) {
        this.communityService.activeParentPosts = (this.communityService.activeParentPosts || []).filter((p: any) => p.key !== post.key);
      } else {
        this.initPosts();
      }
    });
  }

  /** `featuredRailActive()`, held as a field for the template — refreshed in `ngDoCheck`. */
  public showFeaturedRail = false;

  ngDoCheck() {
    const show = this.featuredRailActive();
    if (show !== this.showFeaturedRail) this.showFeaturedRail = show;
  }

  /**
   * True while the Featured rail sits above this feed, which is exactly when featured posts are
   * left out of it — see `featuredRailApplies`. Read at fetch time, so a channel or search
   * change is picked up by the fetch it triggers.
   */
  featuredRailActive(): boolean {
    return (
      this.postType !== "topics" &&
      featuredRailApplies({
        enabled: !!this.env.features?.communityFeaturedRail,
        communityStringId: this.communityStringId,
        betaStringId: this.betaStringId,
        searchQuery: this.searchQuery,
        channelStringId: this.selectedChannel?.string_id,
      })
    );
  }

  buildCommunityAlgoliaFilters(channel?: any): string {
    const parts: string[] = [];
    // Scope the feed to this community. Every post is tagged with `community_string_id`
    // (the backfill script confirmed 62,166/62,167 posts already set at the time of writing;
    // new posts get it via create-post-input in task #34). `community_string_id` must be in
    // the Algolia index's "Attributes for Faceting" for this filter to work.
    if (this.communityStringId) {
      parts.push(`community_string_id:"${this.communityStringId}"`);
    }
    if (channel && channel.string_id !== "all_posts" && channel.string_id !== "pinned_posts" && channel.string_id !== "my_posts") {
      parts.push(`channels:"${channel.string_id}"`);
    }
    if (channel && channel.string_id === "my_posts") {
      parts.push(`author.id:"${this.user?.id}"`);
    }
    return parts.join(" AND ");
  }

  private getHighlightSnippet(highlightResult: any): string {
    const highlightMsg = highlightResult?.message?.value;
    if (!highlightMsg) return "";
    // Temporarily replace highlight spans with placeholders, strip all other HTML, then restore
    const openTag = "<span class='algolia-highlight'>";
    const closeTag = "</span>";
    let cleaned = highlightMsg.replace(new RegExp(openTag, "g"), "\x00OPEN\x00").replace(new RegExp(closeTag, "g"), "\x00CLOSE\x00");
    cleaned = cleaned.replace(/<[^>]*>/g, "");
    cleaned = cleaned.replace(/\x00OPEN\x00/g, openTag).replace(/\x00CLOSE\x00/g, closeTag);
    const tagStart = cleaned.indexOf(openTag);
    if (tagStart === -1) return "";
    const snippetStart = Math.max(0, tagStart - 80);
    const snippetEnd = Math.min(cleaned.length, tagStart + 200);
    let snippet = cleaned.substring(snippetStart, snippetEnd);
    if (snippetStart > 0) snippet = "..." + snippet;
    if (snippetEnd < cleaned.length) snippet = snippet + "...";
    return snippet;
  }

  getChannelString(channels) {
    let string = "<div class='d-flex flex-wrap'>";
    if (!channels?.length) return string + '<span class="item-tag t-12 d-inline-block"># General</span></div>';
    channels.forEach((channel) => {
      if (this.betaService.communityTagMap[channel]) {
        string += '<span class="item-tag t-12 d-inline-block"># ' + this.betaService.communityTagMap[channel].title + "</span>";
      }
    });
    string += "</div>";
    return string;
  }

  getBetaItemStats(post) {
    let type = post.beta_type ? post.beta_type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) : "None";
    let status = post.beta_status ? post.beta_status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) : "None";
    let string = "Type: " + type + " &bull; Status: " + status;
    const jiraHref = post.beta_review_details?.jira_ticket_link || jiraBrowseUrlFromTicketKey(post.beta_review_details?.jira_ticket_number);
    if (post.beta_review_details?.jira_ticket_number && jiraHref && this.isBetaManager) {
      string += ` &bull; Jira Ticket: <a href="${jiraHref}" target="_blank">${post.beta_review_details.jira_ticket_number}</a>`;
    } else if (post.beta_review_details?.jira_ticket_number) {
      string += ` &bull; Jira Ticket: ${post.beta_review_details.jira_ticket_number}`;
    }
    return string;
  }

  /**
   * "Show more posts" is loading. Cleared when the page lands (either path); the timer is a
   * backstop for a load that fails partway — the Algolia chain is nested promises with no
   * single place to catch — so the button can never spin forever.
   */
  public loadingMore = false;
  private _loadingMoreTimer: any = null;

  private _startLoadingMore() {
    this.loadingMore = true;
    clearTimeout(this._loadingMoreTimer);
    this._loadingMoreTimer = setTimeout(() => (this.loadingMore = false), 15000);
  }

  private _endLoadingMore() {
    this.loadingMore = false;
    clearTimeout(this._loadingMoreTimer);
    this._loadingMoreTimer = null;
  }

  showMore() {
    if (this.loadingMore) return;
    this._startLoadingMore();
    this.syncReplyDeferral();
    this.communityService
      .getPostsWithReplies({
        channel: this.selectedChannel,
        collection: this.betaStringId ? "beta_community_posts" : this.env.communityFirestoreCollection,
        betaStringId: this.betaStringId,
        communityStringId: this.betaStringId ? undefined : this.communityStringId,
        excludeFeatured: this.featuredRailActive(),
      })
      .then((result: any) => {
        // Add watchers for newly loaded posts in append mode when using individual watchers
        const useIndividualWatchers = this.usesIndividualWatchers();

        if (useIndividualWatchers && result?.length) {
          const allPostIds = this.collectDisplayedPostIds();
          const collection = this.betaCollection || this.env.communityFirestoreCollection;
          this.communityService.watchDisplayedPosts(allPostIds, collection, true);
        }
      })
      .finally(() => this._endLoadingMore());
  }

  /**
   * Collects all post IDs that are currently displayed (parent posts + replies)
   */
  private collectDisplayedPostIds(): string[] {
    const ids: string[] = [];
    // Add parent post IDs
    this.communityService.activeParentPosts.forEach((post) => {
      if (post.id) ids.push(post.id);
    });
    // Add reply IDs from the thread map
    for (const parentId in this.communityService.activeParentThreadMap) {
      const replies = this.communityService.activeParentThreadMap[parentId];
      if (replies?.length) {
        replies.forEach((reply) => {
          if (reply.id) ids.push(reply.id);
        });
      }
    }
    return ids;
  }

  /**
   * Channel feeds watch the posts on screen one by one, plus new arrivals from now on.
   *
   * All Posts used to subscribe to every document in the community from the last three days
   * instead — a snapshot of hundreds of posts and replies downloaded on every load just to be
   * discarded (`skip(1)`), competing with the feed for bandwidth. The per-post watchers plus
   * `watchNewPosts` see the same changes. Topics (dormant) keep the old watcher.
   */
  private usesIndividualWatchers(): boolean {
    return !!this.betaStringId || this.postType === "channels";
  }

  queuedPostsUpdate(args) {
    this.queuedPosts = args;
  }

  copyLink(msg, event) {
    event.stopPropagation();
    const url =
      this.env.fenderUrl +
      buildCommunityPostPath({
        postId: msg.id,
        communityStringId: msg.community_string_id || this.communityStringId,
        betaStringId: this.betaStringId,
      });
    this._clipboardService.copy(url);
  }

  private subtractExcludedCounts(totalCounts: Record<string, number>, deletedCounts?: Record<string, number>): Record<string, number> {
    if (!deletedCounts) return totalCounts;
    const adjusted = { ...totalCounts };
    for (const channel in deletedCounts) {
      if (adjusted[channel]) {
        adjusted[channel] -= deletedCounts[channel];
        if (adjusted[channel] <= 0) delete adjusted[channel];
      }
    }
    return adjusted;
  }

  private _lastAuthorFacet?: Record<string, number>;

  /**
   * Rebuilds the author dropdown list from the Algolia author.id facet of the current matching set.
   * The author filter itself is excluded from the facet query so picking an author doesn't collapse
   * the dropdown to just that author.
   *
   * Authors not found in betaAuthorMap are filtered out — that map is built from a Firestore query
   * that already excludes deleted posts, so its presence implies "has at least one non-deleted post
   * in this beta community." Without this filter, authors whose posts are all deleted/scheduled
   * would appear in the facet (Algolia can't filter on missing attributes — deleted/scheduled
   * filtering is client-side) and produce zero visible results when selected.
   *
   * While betaAuthorMap is not loaded the list is null (full-list fallback). The map may also come from the
   * per-beta cache, so the list is recomputed from the latest facet once getBetaCommunityDetails resolves.
   */
  private updateFilteredAuthors(authorFacet?: Record<string, number>) {
    if (!this.betaStringId) return;
    this._lastAuthorFacet = authorFacet;
    this.recomputeAuthorsWhenLoaded();
    if (!authorFacet) {
      this.communityService.filteredBetaAuthorList = null;
      return;
    }
    const authorMap = this.communityService.betaAuthorMap;
    if (!authorMap) {
      this.communityService.filteredBetaAuthorList = null;
      return;
    }
    const authors = Object.keys(authorFacet)
      .filter((id) => authorMap[id])
      .map((id) => ({ id, name: authorMap[id].name }))
      .sort((a, b) => a.name.localeCompare(b.name));
    this.communityService.filteredBetaAuthorList = authors;
  }

  private _awaitedAuthorLoad?: Promise<void>;

  /** Redo the author intersection with the latest facet once the in-flight Firestore author load lands (once per load). */
  private recomputeAuthorsWhenLoaded() {
    const load = this.communityService.betaDetailsPromise;
    if (!load || load === this._awaitedAuthorLoad) return;
    this._awaitedAuthorLoad = load;
    load.then(
      () => {
        if (this.communityService.betaAuthorMap) this.updateFilteredAuthors(this._lastAuthorFacet);
      },
      () => {}
    );
  }

  /**
   * Refreshes channel tag counts and the author dropdown from Algolia facets without loading posts.
   * Used by the Firestore recent-replies path (which never hits initPostsFromAlgolia) and after
   * clearing search so sidebar counts/authors match the unfiltered list again.
   */
  refreshBetaFacets(query?: string): Promise<void> {
    if (!this.betaStringId) return Promise.resolve();

    const indexName = "prs_beta_community_posts_sorted_created_desc";
    const baseFilter = "type:post";
    const queryStr = query || "";
    const facetFilters = this.betaCommunityService.buildAlgoliaFilters(this.betaStringId, null, this.user?.id);
    const facetFiltersWithBase = facetFilters ? `${facetFilters} AND ${baseFilter}` : baseFilter;
    const facets = ["channels"];
    const facetRequest: any = {
      indexName,
      query: queryStr,
      filters: facetFiltersWithBase,
      hitsPerPage: 0,
      facets,
    };
    const excludedFacetRequest: any = {
      indexName,
      query: queryStr,
      filters: `${facetFiltersWithBase} AND (deleted.timestamp > 0 OR scheduled_date > ${algoliaNow()})`,
      hitsPerPage: 0,
      facets,
    };
    const authorFacetFilters = this.betaCommunityService.buildAlgoliaFilters(this.betaStringId, this.selectedChannel, this.user?.id, { excludeAuthor: true });
    const authorFacetFiltersWithBase = authorFacetFilters ? `${authorFacetFilters} AND ${baseFilter}` : baseFilter;
    const authorFacetRequest: any = {
      indexName,
      query: queryStr,
      filters: authorFacetFiltersWithBase,
      hitsPerPage: 0,
      facets: ["author.id"],
      maxValuesPerFacet: 1000,
    };

    return this.algoliaClient.search({ requests: [facetRequest, excludedFacetRequest, authorFacetRequest] }).then((res) => {
      const facetResult = res?.results[0] as any;
      const excludedResult = res?.results[1] as any;
      const authorFacetResult = res?.results[2] as any;
      if (facetResult?.facets?.channels) {
        this.communityService.betaTagCountMap = this.subtractExcludedCounts(facetResult.facets.channels, excludedResult?.facets?.channels);
      } else {
        this.communityService.betaTagCountMap = {};
      }
      this.updateFilteredAuthors(authorFacetResult?.facets?.["author.id"]);
    });
  }

  initPosts() {
    // Sort-by-recent-replies is served from Firestore via getPostsByRecentReplies: it fans in
    // from the replies collection so posts with zero replies are naturally excluded and results
    // land ordered by newest reply. The Algolia index has no last-reply timestamp to sort on.
    // Public communities have the same setting as beta now, stored per community on the
    // profile. Push it onto the service before loading: `getPosts` resolves it into
    // `activeSortBy`, which is what the sort and the Firestore/Algolia split both read.
    if (!this.betaStringId) {
      this.communityService.communitySortBy = this.communityService.getCommunitySortMode(this.communityStringId);
    }
    const sortsByRecentReplies = this.betaStringId
      ? this.betaCommunityService.betaSortBy === "recentactivity"
      : this.communityService.getCommunitySortMode(this.communityStringId) === "recentactivity";
    // My Posts and My Pinned Posts never use the reply scan — see the matching exclusion in
    // `getPosts`. Kept in sync here so the component's Algolia/Firestore split agrees with
    // the service's, rather than sending them down a branch that immediately bails.
    const ownershipView = this.selectedChannel?.string_id === "my_posts" || this.selectedChannel?.string_id === "pinned_posts";
    const useRecentRepliesPath = this.postType === "channels" && !this.searchQuery && sortsByRecentReplies && !ownershipView;
    // My Posts and My Pinned Posts both read from Firestore rather than Algolia.
    //
    // Pinned originally had to: Algolia had no filterable `pins` attribute, so the Algolia
    // branch returned everything (regressed in #1345 when Algolia became the primary path —
    // my_posts got its filter added, pinned_posts was overlooked). `pins` is filterable now,
    // but the stronger reason applies to both and did not go away: these are views of YOUR
    // OWN actions, and Algolia is eventually consistent. Publish a post, open My Posts
    // before the index catches up, and it is not there — which reads as data loss. Firestore
    // is read-after-write, so your own post is there the instant you look.
    //
    // `getPosts` queries both properly: `author.id ==` and `pins array-contains`.
    const useOwnershipFirestorePath = this.postType === "channels" && !this.betaStringId && ownershipView;
    // Recent activity from Algolia when the activity replica is configured: one ~20ms query
    // in place of the Firestore fan-in (two scans, a read per parent, then the replies).
    const useActivityIndex = useRecentRepliesPath && this.algoliaIndexFor(true) !== this.algoliaIndexFor(false);
    this.syncReplyDeferral();
    if (this.postType === "channels" && (!useRecentRepliesPath || useActivityIndex) && !useOwnershipFirestorePath) {
      const filters = this.betaStringId
        ? this.betaCommunityService.buildAlgoliaFilters(this.betaStringId, this.selectedChannel, this.user?.id)
        : this.buildCommunityAlgoliaFilters(this.selectedChannel);
      this.initPostsFromAlgolia(filters, this.searchQuery, false, useActivityIndex);
      return;
    }
    this.usingAlgolia = false;
    this.loading = true;
    const channel = this.postType == "channels" ? this.selectedChannel : false;
    const topic = this.postType == "topics" ? this.selectedTopic : false;

    const facetRefresh = useRecentRepliesPath && this.betaStringId ? this.refreshBetaFacets(this.searchQuery || "") : Promise.resolve();

    const setupWatchers = () => {
      const useIndividualWatchers = this.usesIndividualWatchers();

      if (useIndividualWatchers) {
        const displayedPostIds = this.collectDisplayedPostIds();
        const collection = this.betaCollection || this.env.communityFirestoreCollection;
        this.communityService.watchDisplayedPosts(displayedPostIds, collection);
        this.communityService.watchNewPosts(new Date(), collection, this.betaStringId, this.selectedChannel, this.betaStringId ? undefined : this.communityStringId);
      } else {
        this.communityService.watchPostsWithReplies(
          this.selectedChannel,
          this.selectedTopic,
          null,
          null,
          this.betaCollection,
          undefined,
          undefined,
          this.betaStringId ? undefined : this.communityStringId
        );
      }
    };

    Promise.all([
      this.communityService.getPostsWithReplies({
        channel,
        topic,
        limit: this.betaStringId ? 25 : 30,
        clear: true,
        collection: this.betaCollection,
        betaStringId: this.betaStringId,
        communityStringId: this.betaStringId ? undefined : this.communityStringId,
        excludeFeatured: this.featuredRailActive(),
      }),
      facetRefresh,
    ]).then((result: any) => {
      this.loading = false;
      this.recomputeUnreadPosts();
      this.postsLoaded.next(true);
      setupWatchers();
    });

    if (
      this.selectedChannel &&
      this.communityService.channelSplashes[this.selectedChannel.string_id] &&
      this.communityService.channelSplashes[this.selectedChannel.string_id].indexOf('id="youtubelink"') > -1
    ) {
      this._win.setTimeout(() => {
        let link = document.getElementById("youtubelink");
        if (link) {
          let youtubeID = link.getAttribute("data-youtube-id");
          link.addEventListener("click", () => {
            this.showVideo(youtubeID);
          });
        }
      }, 100);
    }
    if (this.selectedTopic && this.selectedTopic.description && this.selectedTopic.description.indexOf('id="youtubelink"') > -1) {
      this._win.setTimeout(() => {
        let link = document.getElementById("youtubelink");
        if (link) {
          let youtubeID = link.getAttribute("data-youtube-id");
          link.addEventListener("click", () => {
            this.showVideo(youtubeID);
          });
        }
      }, 100);
    }
  }

  initPostsFromAlgolia(filters: string, query?: string, append?: boolean, activity?: boolean) {
    if (!append) this.loading = true;
    this.usingAlgolia = true;
    this.currentAlgoliaFilters = filters;
    this.currentAlgoliaQuery = query || "";

    if (!append) {
      this.algoliaPage = 0;
      this.algoliaTotalPages = 0;
      this.usingActivityIndex = !!activity;
      // `getPostsWithReplies` resolves this on the Firestore path; this path never goes
      // through it. Without it, every re-sort — on load, and on each live update — used
      // whatever ordering the previous feed left behind, so a thread that just got a reply
      // stayed put in a recent-activity feed.
      this.communityService.activeSortBy = this.betaStringId ? this.betaCommunityService.betaSortBy : this.communityService.communitySortBy;
    }
    this.syncReplyDeferral();

    const indexName = this.algoliaIndexFor(this.usingActivityIndex);
    const baseFilter = "type:post";
    const mainFilters = filters ? `${filters} AND ${baseFilter}` : baseFilter;
    const requestParams: any = {
      indexName,
      query: this.currentAlgoliaQuery,
      filters: mainFilters,
      page: this.algoliaPage,
      hitsPerPage: 25,
    };

    if (!this.currentAlgoliaQuery) {
      requestParams.relevancyStrictness = 0;
    }

    // The activity replica is virtual: it sorts on `last_activity_at` only among the hits the
    // engine collected, and with no facets requested it stops collecting early on a big index
    // (`exhaustive.nbHits: false`). Recently active threads then vanish from page one. Asking
    // for any facet makes the evaluation exhaustive, for ~3ms.
    if (this.usingActivityIndex) {
      requestParams.facets = ["type"];
      requestParams.maxValuesPerFacet = 1;
    }

    // Add highlight params when there's a text query. Without one, ask for none: by default
    // Algolia highlights every searchable attribute anyway, which was ~30% of a feed page.
    if (this.currentAlgoliaQuery) {
      requestParams.highlightPreTag = "<span class='algolia-highlight'>";
      requestParams.highlightPostTag = "</span>";
      requestParams.attributesToHighlight = ["message", "subject"];
    } else {
      requestParams.attributesToHighlight = [];
      requestParams.attributesToSnippet = [];
    }

    const facetsPromise = !append && this.betaStringId ? this.refreshBetaFacets(query || "") : Promise.resolve();

    // Count deleted/scheduled posts with the same filters as the main query for accurate result
    // count. First page only — the count doesn't change as you page.
    const mainExcludedCountRequest: any = {
      indexName,
      query: this.currentAlgoliaQuery,
      filters: `${mainFilters} AND (deleted.timestamp > 0 OR scheduled_date > ${algoliaNow()})`,
      hitsPerPage: 0,
    };

    let algoliaArgs = {
      requests: append ? [requestParams] : [requestParams, mainExcludedCountRequest],
    };
    const search = this.algoliaClient.search(algoliaArgs).catch((error) => {
      // The activity replica not existing yet (or any failure of it) must not leave the feed
      // empty: stop asking it for this session and reload down the Firestore path.
      if (this.usingActivityIndex) {
        console.warn("[community] activity index failed, falling back to Firestore", error);
        ChannelComponent.activityIndexFailed = true;
        this.usingActivityIndex = false;
        if (append) this._endLoadingMore();
        this.initPosts();
      } else {
        // Any other failure used to leave the spinner up for good.
        console.warn("[community] Algolia feed query failed", error);
        this.loading = false;
        if (append) this._endLoadingMore();
      }
      return null;
    });
    search.then((res) => {
      if (!res) return;
      const result = res?.results[0] as any;
      const mainExcludedResult = res?.results[1] as any;
      const { hits, nbPages } = result;
      this.algoliaTotalPages = nbPages;
      if (!append) {
        const total = result.nbSortedHits ?? result.nbHits ?? 0;
        const excluded = mainExcludedResult?.nbSortedHits ?? mainExcludedResult?.nbHits ?? 0;
        this.algoliaResultCount = total - excluded;
      }
      // Filter out deleted and future scheduled posts, separate parents from replies
      const now = Date.now();
      const isMyPosts = this.selectedChannel?.string_id === "my_posts";
      const isPinnedChannel = this.selectedChannel?.string_id === "pinned_posts";
      const excludeFeatured = this.featuredRailActive();
      const parentHits: any[] = [];
      const replyHits: any[] = [];
      hits.forEach((hit: any) => {
        if (hit.deleted) return;
        if (hit.scheduled_date && hit.scheduled_date > now && !(isMyPosts && hit.author?.id === this.user?.id)) return;
        // Pinned posts render in the announcements section at the top; exclude from the main feed
        // unless the user is specifically viewing the "Pinned Posts" channel.
        if (this.betaStringId && hit.pinned && !isPinnedChannel) return;
        // Featured posts sit in the rail above this feed (DENG-1294).
        if (excludeFeatured && isFeaturedNow({ ...hit, id: hit.objectID })) return;
        if (this.betaStringId && hit.author?.id && hit.author?.name && this.communityService.betaAuthorMap && !this.communityService.betaAuthorMap[hit.author.id]) {
          this.communityService.betaAuthorMap[hit.author.id] = hit.author;
        }
        if (hit.parentID) {
          replyHits.push(hit);
        } else {
          parentHits.push(hit);
        }
      });
      const wrapTimestamp = (val: any) => {
        if (!val || (typeof val === "object" && typeof val.toDate === "function")) return val;
        if (typeof val === "number") {
          const ts = val < 1e12 ? val * 1000 : val;
          const date = new Date(ts);
          return { toDate: () => date, seconds: Math.floor(ts / 1000) };
        } else if (typeof val === "string") {
          const date = new Date(val);
          return { toDate: () => date, seconds: Math.floor(date.getTime() / 1000) };
        }
        return val;
      };

      const mapHitToPost = (hit: any) => {
        const post = { ...hit };
        post.key = hit.objectID;
        post.id = hit.objectID;
        post.created = wrapTimestamp(post.created);
        post.scheduled_date = wrapTimestamp(post.scheduled_date);
        // Same shape as the Firestore Timestamps a watcher will later write over them.
        post.last_reply_at = wrapTimestamp(post.last_reply_at);
        post.last_activity_at = wrapTimestamp(post.last_activity_at);
        if (post.scheduled_date?.seconds && post.scheduled_date.seconds * 1000 < this.currentTime) {
          post.scheduled_date = null;
        }
        if (post.beta_review_details) {
          post.beta_review_details = { ...post.beta_review_details };
          post.beta_review_details.reviewed_on = wrapTimestamp(post.beta_review_details.reviewed_on);
        }
        if (this.currentAlgoliaQuery && hit._highlightResult) {
          if (hit._highlightResult.subject?.value) {
            post._rawSubject = post.subject;
            post.subject = hit._highlightResult.subject.value;
          }
          post.highlightSnippet = this.getHighlightSnippet(hit._highlightResult);
        }
        if (this.currentAlgoliaQuery) {
          post._searchQuery = this.currentAlgoliaQuery;
        }
        return post;
      };

      const posts = parentHits.map(mapHitToPost);
      const parentIdSet = new Set(posts.map((p) => p.id));

      // When searching, fetch parent posts for any reply hits whose parent isn't already in results
      const collectionName = this.betaCollection || this.env.communityFirestoreCollection;
      const missingParentIds = this.currentAlgoliaQuery ? [...new Set(replyHits.map((r) => r.parentID).filter((id) => !parentIdSet.has(id)))] : [];
      const parentFetchPromises = missingParentIds.map((id) => this.communityService.getPostByID(id, true, false, collectionName));
      Promise.all(parentFetchPromises).then((fetchedParents: any[]) => {
        fetchedParents.forEach((parent) => {
          if (parent && !parent.deleted) {
            parent.key = parent.key || parent.id;
            posts.push(parent);
            parentIdSet.add(parent.id);
          }
        });

        // Replies for the page, batched — or none at all for list rows whose parent carries
        // its own summary. See `loadFeedReplies`.
        const collectionRef = this.fbService.collection(collectionName);
        this.communityService
          .loadFeedReplies(posts, collectionRef.ref, () => false)
          .then((allReplies) => {
            const postsWithReplies = [...posts, ...allReplies];
            this.communityService.processPostsAndReplies(postsWithReplies, !append, undefined, undefined, this.betaStringId);

            // Skip — author list is rebuilt once from the facet result below on initial load
            // (not on pagination), outside this reply-fetch promise chain.

            this.communityService.activePostsLimitReached = this.algoliaPage >= this.algoliaTotalPages - 1;
            facetsPromise.then(() => {
              this.loading = false;
              if (append) this._endLoadingMore();
              this.recomputeUnreadPosts();
              this.postsLoaded.next({ append: !!append });
            });

            // Set up Firebase watchers for real-time updates
            if (!append) {
              const displayedPostIds = this.collectDisplayedPostIds();
              this.communityService.watchDisplayedPosts(displayedPostIds, collectionName);
              this.communityService.watchNewPosts(new Date(), collectionName, this.betaStringId, this.selectedChannel, this.betaStringId ? undefined : this.communityStringId);
            } else {
              const displayedPostIds = this.collectDisplayedPostIds();
              this.communityService.watchDisplayedPosts(displayedPostIds, collectionName, true);
            }
          });
      });
    });
  }

  showMoreAlgolia() {
    if (this.loadingMore) return;
    this._startLoadingMore();
    this.algoliaPage++;
    this.initPostsFromAlgolia(this.currentAlgoliaFilters, this.currentAlgoliaQuery, true);
  }

  goToPost(post) {
    this.communityService.saveCommunityListBackCache(this.betaStringId, this.selectedChannel);
    this.communityService.enrichCommunityListBackCacheForPostNavigation(
      post.id,
      this.usingAlgolia
        ? {
            algoliaPage: this.algoliaPage,
            algoliaTotalPages: this.algoliaTotalPages,
            currentAlgoliaFilters: this.currentAlgoliaFilters,
            currentAlgoliaQuery: this.currentAlgoliaQuery,
            usingActivityIndex: this.usingActivityIndex,
          }
        : undefined
    );
    this.router.navigate([`/beta/betapost/${post.id}`], { queryParams: { betaStringId: this.betaStringId } });
  }

  showVideo(id, type = "youtube") {
    this.eventBusService.emit(this.eventBusService.types.playVideo, {
      id: id,
      type: type,
    });
  }

  /**
   * Only an FSP-gated community paywalls the second page. Beta bypasses (membership is the
   * gate), as do guests — they already saw "Show more" before this existed.
   */
  private updatePagingGate() {
    if (this.betaStringId || !this.user || this.user.active_subscription) {
      this.pagingNeedsSubscription = false;
      return;
    }
    const community = this.communitiesService.getCommunity(this.communityStringId);
    this.pagingNeedsSubscription = community?.participation_condition === "fsp_plus_subscriber";
  }

  upgrade() {
    this.router.navigate(["/onboarding"], { queryParams: { subscribeOnly: true } });
  }

  ngOnDestroy() {
    clearTimeout(this._loadingMoreTimer);
    if (!this.communityService.restoringFromPostDetail) {
      this.communityService.unwatch();
    } else {
      // Just clean up subscriptions, don't clear the restored post data
      this.communityService.unwatchDisplayedPosts();
      this.communityService.unwatchNewPosts();
    }
  }
}
